import React from "react";

import {
  FiAlertTriangle,
  FiBox,
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiEye,
  FiImage,
  FiPackage,
  FiPlus,
  FiPower,
  FiRefreshCcw,
  FiTrash2,
} from "react-icons/fi";


/* =========================================================
   COMPONENT
========================================================= */

const ProductTable = ({
  products = [],

  loading = false,

  pagination,

  onView,

  onEdit,

  onAdjustStock,

  onDelete,

  onChangeStatus,

  onPageChange,

  onLimitChange,

  onAddProduct,
}) => {
  /* =======================================================
     PAGINATION
  ======================================================= */

  const safePagination = {
    page:
      Number(
        pagination?.page ||
        1
      ),

    limit:
      Number(
        pagination?.limit ||
        10
      ),

    total:
      Number(
        pagination?.total ||
        0
      ),

    totalPages:
      Math.max(
        Number(
          pagination?.totalPages ||
          1
        ),

        1
      ),
  };


  /* =======================================================
     FORMAT CURRENCY
  ======================================================= */

  const formatCurrency =
    (
      value
    ) => {
      return new Intl.NumberFormat(
        "en-IN",
        {
          style:
            "currency",

          currency:
            "INR",

          maximumFractionDigits:
            0,
        }
      ).format(
        Number(
          value ||
          0
        )
      );
    };


  /* =======================================================
     FORMAT DATE
  ======================================================= */

  const formatDate =
    (
      value
    ) => {
      if (!value) {
        return "-";
      }


      const date =
        new Date(
          value
        );


      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return "-";
      }


      return new Intl.DateTimeFormat(
        "en-IN",
        {
          day:
            "2-digit",

          month:
            "short",

          year:
            "numeric",
        }
      ).format(
        date
      );
    };


  /* =======================================================
     SERIAL NUMBER
  ======================================================= */

  const getSerialNumber =
    (
      index
    ) => {
      return (
        (
          safePagination.page -
          1
        ) *
          safePagination.limit +
        index +
        1
      );
    };


  /* =======================================================
     STOCK VALUES
  ======================================================= */

  const getStockValues =
    (
      product
    ) => {
      const stockQty =
        Math.max(
          Number(
            product?.stockQty ||
            0
          ),

          0
        );


      const reservedQty =
        Math.max(
          Number(
            product?.reservedQty ||
            0
          ),

          0
        );


      const availableQty =
        Math.max(
          stockQty -
            reservedQty,

          0
        );


      const threshold =
        Math.max(
          Number(
            product
              ?.lowStockThreshold ||
            0
          ),

          0
        );


      return {
        stockQty,

        reservedQty,

        availableQty,

        threshold,
      };
    };


  /* =======================================================
     STOCK CLASS
  ======================================================= */

  const getStockClass =
    (
      product
    ) => {
      const {
        availableQty,
        threshold,
      } =
        getStockValues(
          product
        );


      if (
        availableQty <=
        0
      ) {
        return "inventory-product-stock-out";
      }


      if (
        availableQty <=
        threshold
      ) {
        return "inventory-product-stock-low";
      }


      return "inventory-product-stock-ok";
    };


  /* =======================================================
     STOCK LABEL
  ======================================================= */

  const getStockLabel =
    (
      product
    ) => {
      const {
        availableQty,
        threshold,
      } =
        getStockValues(
          product
        );


      if (
        availableQty <=
        0
      ) {
        return "Out of Stock";
      }


      if (
        availableQty <=
        threshold
      ) {
        return "Low Stock";
      }


      return "In Stock";
    };


  /* =======================================================
     STATUS CLASS
  ======================================================= */

  const getStatusClass =
    (
      status
    ) => {
      switch (
        status
      ) {
        case "active":
          return "inventory-product-status-active";


        case "inactive":
          return "inventory-product-status-inactive";


        case "draft":
          return "inventory-product-status-draft";


        default:
          return "inventory-product-status-inactive";
      }
    };


  /* =======================================================
     NEXT STATUS
  ======================================================= */

  const getNextStatus =
    (
      status
    ) => {
      return status ===
        "active"
        ? "inactive"
        : "active";
    };


  /* =======================================================
     CONDITION LABEL
  ======================================================= */

  const getConditionLabel =
    (
      condition
    ) => {
      const labels = {
        new:
          "New",

        used:
          "Used",

        refurbished:
          "Refurbished",

        "open-box":
          "Open Box",
      };


      return (
        labels[
          condition
        ] ||
        "-"
      );
    };


  /* =======================================================
     CATEGORY
  ======================================================= */

  const getCategoryName =
    (
      product
    ) => {
      if (
        product?.category &&
        typeof product.category ===
          "object"
      ) {
        return (
          product.category
            .name ||
          "-"
        );
      }


      return "-";
    };


  /* =======================================================
     CHILD CATEGORY
  ======================================================= */

  const getSubCategoryName =
    (
      product
    ) => {
      if (
        product
          ?.subCategory &&
        typeof product.subCategory ===
          "object"
      ) {
        return (
          product
            .subCategory
            .name ||
          "-"
        );
      }


      return "";
    };


  /* =======================================================
     THUMBNAIL
  ======================================================= */

  const getThumbnail =
    (
      product
    ) => {
      return (
        product?.thumbnail ||
        product?.images
          ?.[0] ||
        ""
      );
    };


  /* =======================================================
     IMAGE URL
  ======================================================= */

  const getImageUrl =
    (
      image
    ) => {
      if (!image) {
        return "";
      }


      if (
        /^https?:\/\//i.test(
          image
        )
      ) {
        return image;
      }


      const baseUrl =
        String(
          process.env
            .REACT_APP_API_URL ||
          ""
        ).replace(
          /\/+$/,
          ""
        );


      const imagePath =
        String(
          image
        ).replace(
          /^\/+/,
          ""
        );


      return baseUrl
        ? `${baseUrl}/${imagePath}`
        : `/${imagePath}`;
    };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-product-table-card"
    >
      <div
        className="inventory-product-table-scroll"
      >
        <table
          className="inventory-product-table"
        >
          <thead>
            <tr>
              <th
                style={{
                  width:
                    65,
                }}
              >
                S.No
              </th>


              <th>
                Product
              </th>


              <th>
                SKU / Barcode
              </th>


              <th>
                Category
              </th>


              <th>
                Pricing
              </th>


              <th>
                Inventory
              </th>


              <th>
                Condition
              </th>


              <th>
                Status
              </th>


              <th>
                Created On
              </th>


              <th
                style={{
                  width:
                    235,
                }}
              >
                Action
              </th>
            </tr>
          </thead>


          <tbody>
            {/* =================================================
                LOADING
            ================================================= */}

            {loading ? (
              <tr>
                <td
                  colSpan={
                    10
                  }
                  className="inventory-product-table-message"
                >
                  <div
                    className="inventory-product-table-loading"
                  >
                    <span
                      className="inventory-product-table-spinner"
                    />

                    Loading products...
                  </div>
                </td>
              </tr>
            ) : products.length ===
              0 ? (
              /* ===============================================
                 EMPTY
              =============================================== */

              <tr>
                <td
                  colSpan={
                    10
                  }
                  className="inventory-product-table-message"
                >
                  <div
                    className="inventory-product-table-empty"
                  >
                    <FiPackage />


                    <strong>
                      No Products Found
                    </strong>


                    <span>
                      Add your first inventory
                      product to start managing
                      stock.
                    </span>


                    <button
                      type="button"
                      onClick={
                        onAddProduct
                      }
                    >
                      <FiPlus />

                      Add Product
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              products.map(
                (
                  product,
                  index
                ) => {
                  const thumbnail =
                    getThumbnail(
                      product
                    );


                  const sellingPrice =
                    Number(
                      product
                        ?.sellingPrice ||
                        0
                    );


                  const discountedPrice =
                    Number(
                      product
                        ?.discountedPrice ||
                        0
                    );


                  const purchasePrice =
                    Number(
                      product
                        ?.purchasePrice ||
                        0
                    );


                  const {
                    stockQty,

                    reservedQty,

                    availableQty,

                    threshold,
                  } =
                    getStockValues(
                      product
                    );


                  const subCategoryName =
                    getSubCategoryName(
                      product
                    );


                  return (
                    <tr
                      key={
                        product?._id ||
                        index
                      }
                    >
                      {/* =====================================
                          S.NO
                      ===================================== */}

                      <td
                        className="inventory-product-table-serial"
                      >
                        {getSerialNumber(
                          index
                        )}
                      </td>


                      {/* =====================================
                          PRODUCT
                      ===================================== */}

                      <td>
                        <div
                          className="inventory-product-table-product"
                        >
                          <div
                            className="inventory-product-table-image"
                          >
                            {thumbnail ? (
                              <img
                                src={
                                  getImageUrl(
                                    thumbnail
                                  )
                                }
                                alt={
                                  product?.name ||
                                  "Product"
                                }
                                onError={(
                                  event
                                ) => {
                                  event.currentTarget.style.display =
                                    "none";
                                }}
                              />
                            ) : (
                              <FiImage />
                            )}
                          </div>


                          <div
                            className="inventory-product-table-product-info"
                          >
                            <strong
                              title={
                                product?.name ||
                                ""
                              }
                            >
                              {product?.name ||
                                "-"}
                            </strong>


                            <span>
                              {product?.brand ||
                                "No brand"}

                              {product
                                ?.modelNumber
                                ? ` • ${product.modelNumber}`
                                : ""}
                            </span>


                            {product
                              ?.isFeatured ? (
                              <small>
                                Featured Product
                              </small>
                            ) : null}
                          </div>
                        </div>
                      </td>


                      {/* =====================================
                          SKU
                      ===================================== */}

                      <td>
                        <div
                          className="inventory-product-table-code"
                        >
                          <strong>
                            {product?.sku ||
                              "-"}
                          </strong>


                          <span>
                            {product?.barcode ||
                              "No barcode"}
                          </span>
                        </div>
                      </td>


                      {/* =====================================
                          CATEGORY
                      ===================================== */}

                      <td>
                        <div
                          className="inventory-product-table-category"
                        >
                          <strong>
                            {getCategoryName(
                              product
                            )}
                          </strong>


                          {subCategoryName ? (
                            <span
                              className="inventory-product-table-category-child"
                            >
                              ↳{" "}

                              {
                                subCategoryName
                              }
                            </span>
                          ) : (
                            <span>
                              No child category
                            </span>
                          )}
                        </div>
                      </td>


                      {/* =====================================
                          PRICE
                      ===================================== */}

                      <td>
                        <div
                          className="inventory-product-table-price"
                        >
                          <strong>
                            {formatCurrency(
                              discountedPrice >
                                  0 &&
                                discountedPrice <
                                  sellingPrice
                                ? discountedPrice
                                : sellingPrice
                            )}
                          </strong>


                          {discountedPrice >
                            0 &&
                          discountedPrice <
                            sellingPrice ? (
                            <span>
                              {formatCurrency(
                                sellingPrice
                              )}
                            </span>
                          ) : null}


                          <small>
                            Cost:{" "}

                            {formatCurrency(
                              purchasePrice
                            )}
                          </small>
                        </div>
                      </td>


                      {/* =====================================
                          INVENTORY
                      ===================================== */}

                      <td>
                        <div
                          className="inventory-product-table-stock"
                        >
                          <div
                            className="inventory-product-table-stock-main"
                          >
                            <FiBox />


                            <strong>
                              {availableQty}
                            </strong>


                            <small>
                              available
                            </small>
                          </div>


                          <span
                            className={getStockClass(
                              product
                            )}
                          >
                            {availableQty <=
                            0 ? (
                              <FiAlertTriangle />
                            ) : null}

                            {getStockLabel(
                              product
                            )}
                          </span>


                          <div
                            className="inventory-product-table-stock-meta"
                          >
                            <small>
                              Total:{" "}

                              <strong>
                                {stockQty}
                              </strong>
                            </small>


                            <small>
                              Reserved:{" "}

                              <strong>
                                {reservedQty}
                              </strong>
                            </small>


                            <small>
                              Alert at:{" "}

                              <strong>
                                {threshold}
                              </strong>
                            </small>
                          </div>
                        </div>
                      </td>


                      {/* =====================================
                          CONDITION
                      ===================================== */}

                      <td>
                        <span
                          className="inventory-product-condition"
                        >
                          {getConditionLabel(
                            product
                              ?.condition
                          )}
                        </span>
                      </td>


                      {/* =====================================
                          STATUS
                      ===================================== */}

                      <td>
                        <span
                          className={`inventory-product-status ${getStatusClass(
                            product
                              ?.status
                          )}`}
                        >
                          {product?.status ===
                          "active"
                            ? "Active"
                            : product?.status ===
                                "draft"
                              ? "Draft"
                              : "Inactive"}
                        </span>
                      </td>


                      {/* =====================================
                          CREATED
                      ===================================== */}

                      <td>
                        <span
                          className="inventory-product-created-date"
                        >
                          {formatDate(
                            product
                              ?.createdAt
                          )}
                        </span>
                      </td>


                      {/* =====================================
                          ACTIONS
                      ===================================== */}

                      <td>
                        <div
                          className="inventory-product-actions"
                        >
                          {/* VIEW */}

                          <button
                            type="button"
                            className="inventory-product-action inventory-product-action-view"
                            title="View product"
                            onClick={() =>
                              onView?.(
                                product
                              )
                            }
                          >
                            <FiEye />
                          </button>


                          {/* EDIT */}

                          <button
                            type="button"
                            className="inventory-product-action inventory-product-action-edit"
                            title="Edit product"
                            onClick={() =>
                              onEdit?.(
                                product
                              )
                            }
                          >
                            <FiEdit2 />
                          </button>


                          {/* STOCK ADJUSTMENT */}

                          <button
                            type="button"
                            className="inventory-product-action inventory-product-action-stock"
                            title="Adjust stock"
                            onClick={() =>
                              onAdjustStock?.(
                                product
                              )
                            }
                          >
                            <FiRefreshCcw />
                          </button>


                          {/* STATUS */}

                          <button
                            type="button"
                            className="inventory-product-action inventory-product-action-status"
                            title={
                              product?.status ===
                              "active"
                                ? "Make inactive"
                                : "Make active"
                            }
                            onClick={() =>
                              onChangeStatus?.(
                                product,

                                getNextStatus(
                                  product
                                    ?.status
                                )
                              )
                            }
                          >
                            <FiPower />
                          </button>


                          {/* DELETE */}

                          <button
                            type="button"
                            className="inventory-product-action inventory-product-action-delete"
                            title="Delete product"
                            onClick={() =>
                              onDelete?.(
                                product
                              )
                            }
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
              )
            )}
          </tbody>
        </table>
      </div>


      {/* ===================================================
          PAGINATION
      =================================================== */}

      {!loading &&
      safePagination.total >
        0 ? (
        <div
          className="inventory-product-pagination"
        >
          <div
            className="inventory-product-pagination-summary"
          >
            Showing{" "}

            <strong>
              {(
                safePagination.page -
                1
              ) *
                safePagination.limit +
                1}
            </strong>

            {" to "}

            <strong>
              {Math.min(
                safePagination.page *
                  safePagination.limit,

                safePagination.total
              )}
            </strong>

            {" of "}

            <strong>
              {
                safePagination.total
              }
            </strong>

            {" products"}
          </div>


          <div
            className="inventory-product-pagination-controls"
          >
            <select
              value={
                safePagination.limit
              }
              onChange={
                onLimitChange
              }
              aria-label="Products per page"
            >
              <option
                value={
                  10
                }
              >
                10 / page
              </option>


              <option
                value={
                  20
                }
              >
                20 / page
              </option>


              <option
                value={
                  50
                }
              >
                50 / page
              </option>


              <option
                value={
                  100
                }
              >
                100 / page
              </option>
            </select>


            <button
              type="button"
              disabled={
                safePagination.page <=
                1
              }
              onClick={() =>
                onPageChange?.(
                  safePagination.page -
                    1
                )
              }
              title="Previous page"
            >
              <FiChevronLeft />
            </button>


            <span>
              Page{" "}

              <strong>
                {
                  safePagination.page
                }
              </strong>

              {" of "}

              <strong>
                {
                  safePagination.totalPages
                }
              </strong>
            </span>


            <button
              type="button"
              disabled={
                safePagination.page >=
                safePagination.totalPages
              }
              onClick={() =>
                onPageChange?.(
                  safePagination.page +
                    1
                )
              }
              title="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};


export default ProductTable;